import { c as _c } from "react/compiler-runtime";
import React from 'react';
import { InstructorDetails } from '@udemy-v2/browse-course';
import styles from './instructor-stats.module.scss';
import { Image } from '@udemy-v2/react-core-components';
import { useFormatNumber, useI18n } from '@udemy-v2/i18n';
import { Icon } from '@udemy-v2/icons';
import { BlockList, BlockListItem } from '@udemy-v2/react-core-components';
import { useClpCourse } from '@/features/course-landing-page/context/clp-course-provider.react-component';
import { CommunityPoweredBadge } from '../../instructor-community/community-powered-badge';
export interface InstructorStatsProps {
  instructor: InstructorDetails;
  showCommunityPoweredBadge?: boolean;
}

/**
 * InstructorStats component displays statistical information about a course instructor.
 *
 * @component
 * @example
 * ```jsx
 * <InstructorStats instructor={instructor} />
 * ```
 *
 * @param {InstructorStatsProps} props - The properties for the component.
 * @param {InstructorDetails} props.instructor - The instructor object containing statistical data.
 *
 * @returns {JSX.Element} A React component that renders the instructor's statistics including
 * total courses, total students, and average rating.
 */

export const InstructorStats: React.FC<InstructorStatsProps> = t0 => {
  const $ = _c(60);
  const {
    instructor,
    showCommunityPoweredBadge: t1
  } = t0;
  const showCommunityPoweredBadge = t1 === undefined ? false : t1;
  const {
    ninterpolate,
    interpolate,
    gettext
  } = useI18n();
  const {
    formatNumber
  } = useFormatNumber();
  const course = useClpCourse();
  const isGwgCourse = !!course?.contentCollectionInfo?.isInGooglePartnerCollection;
  const t2 = instructor.stats?.rating ?? 0;
  let T0;
  let T1;
  let t10;
  let t11;
  let t12;
  let t13;
  let t14;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== formatNumber || $[1] !== gettext || $[2] !== instructor.images.large || $[3] !== instructor.name || $[4] !== instructor.stats || $[5] !== instructor.url || $[6] !== interpolate || $[7] !== isGwgCourse || $[8] !== ninterpolate || $[9] !== showCommunityPoweredBadge || $[10] !== t2) {
    const starRatingText = formatNumber(t2.toFixed(1));
    const numStudentText = instructor.stats?.enrollmentCount === 0 || instructor.stats?.enrollmentCount === undefined ? 0 : formatNumber(instructor.stats.enrollmentCount);
    const numCourseText = instructor.stats?.courseCount === 0 || instructor.stats?.courseCount === undefined ? 0 : formatNumber(instructor.stats.courseCount);
    const numReviewText = instructor.stats?.reviewCount === 0 || instructor.stats?.reviewCount === undefined ? 0 : formatNumber(instructor.stats.reviewCount);
    let t15;
    if ($[25] !== instructor.images.large || $[26] !== instructor.name || $[27] !== instructor.url || $[28] !== showCommunityPoweredBadge) {
      t15 = () => {
        if (showCommunityPoweredBadge && instructor.images.large) {
          return <CommunityPoweredBadge imageUrl={instructor.images.large} displayName={instructor.name} url={instructor.url} size="lg" className={styles["instructor-stats-badge"]} />;
        }
        return <Image src={instructor.images.large} alt={instructor.name} className={styles["instructor-stats-image"]} />;
      };
      $[25] = instructor.images.large;
      $[26] = instructor.name;
      $[27] = instructor.url;
      $[28] = showCommunityPoweredBadge;
      $[29] = t15;
    } else {
      t15 = $[29];
    }
    const renderInstructorPhoto = t15;
    t14 = styles["instructor-stats"];
    t12 = styles["instructor-stats-row"];
    if ($[30] !== renderInstructorPhoto) {
      t13 = renderInstructorPhoto();
      $[30] = renderInstructorPhoto;
      $[31] = t13;
    } else {
      t13 = $[31];
    }
    T1 = BlockList;
    t7 = "small";
    t8 = "tight";
    let t16;
    if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = <Icon label={false} glyph="rating-star" />;
      $[32] = t16;
    } else {
      t16 = $[32];
    }
    const t17 = interpolate(gettext("%(rating)s Instructor Rating"), {
      rating: starRatingText
    }, true);
    if ($[33] !== t17) {
      t9 = <BlockListItem data-purpose="stat" key="instructor-stats-rating" icon={t16}>{t17}</BlockListItem>;
      $[33] = t17;
      $[34] = t9;
    } else {
      t9 = $[34];
    }
    let t18;
    if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
      t18 = <Icon label={false} glyph="certificate" />;
      $[35] = t18;
    } else {
      t18 = $[35];
    }
    const t19 = ninterpolate(gettext("%(count)s Review"), gettext("%(count)s Reviews"), instructor.stats?.reviewCount ?? 0, {
      count: numReviewText
    });
    if ($[36] !== t19) {
      t10 = <BlockListItem data-purpose="stat" key="instructor-stats-reviews" icon={t18}>{t19}</BlockListItem>;
      $[36] = t19;
      $[37] = t10;
    } else {
      t10 = $[37];
    }
    t11 = !isGwgCourse && <BlockListItem data-purpose="stat" key="instructor-stats-students" icon={<Icon label={false} glyph="people" />}>{ninterpolate(gettext("%(count)s Student"), gettext("%(count)s Students"), instructor.stats?.enrollmentCount ?? 0, {
        count: numStudentText
      })}</BlockListItem>;
    T0 = BlockListItem;
    t3 = "stat";
    t4 = "instructor-stats-courses";
    if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <Icon label={false} glyph="play" />;
      $[38] = t5;
    } else {
      t5 = $[38];
    }
    t6 = ninterpolate(gettext("%(count)s Course"), gettext("%(count)s Courses"), instructor.stats?.courseCount ?? 0, {
      count: numCourseText
    });
    $[0] = formatNumber;
    $[1] = gettext;
    $[2] = instructor.images.large;
    $[3] = instructor.name;
    $[4] = instructor.stats;
    $[5] = instructor.url;
    $[6] = interpolate;
    $[7] = isGwgCourse;
    $[8] = ninterpolate;
    $[9] = showCommunityPoweredBadge;
    $[10] = t2;
    $[11] = T0;
    $[12] = T1;
    $[13] = t10;
    $[14] = t11;
    $[15] = t12;
    $[16] = t13;
    $[17] = t14;
    $[18] = t3;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
    $[24] = t9;
  } else {
    T0 = $[11];
    T1 = $[12];
    t10 = $[13];
    t11 = $[14];
    t12 = $[15];
    t13 = $[16];
    t14 = $[17];
    t3 = $[18];
    t4 = $[19];
    t5 = $[20];
    t6 = $[21];
    t7 = $[22];
    t8 = $[23];
    t9 = $[24];
  }
  let t15;
  if ($[39] !== T0 || $[40] !== t3 || $[41] !== t4 || $[42] !== t5 || $[43] !== t6) {
    t15 = <T0 data-purpose={t3} key={t4} icon={t5}>{t6}</T0>;
    $[39] = T0;
    $[40] = t3;
    $[41] = t4;
    $[42] = t5;
    $[43] = t6;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== T1 || $[46] !== t10 || $[47] !== t11 || $[48] !== t15 || $[49] !== t7 || $[50] !== t8 || $[51] !== t9) {
    t16 = <T1 size={t7} padding={t8}>{t9}{t10}{t11}{t15}</T1>;
    $[45] = T1;
    $[46] = t10;
    $[47] = t11;
    $[48] = t15;
    $[49] = t7;
    $[50] = t8;
    $[51] = t9;
    $[52] = t16;
  } else {
    t16 = $[52];
  }
  let t17;
  if ($[53] !== t12 || $[54] !== t13 || $[55] !== t16) {
    t17 = <div className={t12}>{t13}{t16}</div>;
    $[53] = t12;
    $[54] = t13;
    $[55] = t16;
    $[56] = t17;
  } else {
    t17 = $[56];
  }
  let t18;
  if ($[57] !== t14 || $[58] !== t17) {
    t18 = <div className={t14}>{t17}</div>;
    $[57] = t14;
    $[58] = t17;
    $[59] = t18;
  } else {
    t18 = $[59];
  }
  return t18;
};